import "../../styles/templates/_product.scss";
import "../../styles/components/_product-option.scss";
import { BehaviorSubject, filter, tap, Subscription, fromEvent } from "rxjs";
import { getElementOrParentByClassName, showInlineCart } from "../helpers/utils";
import { isMobile } from "../vue/constants/devices";
import { QUICKSHOP_REFRESH, QUICKSHOP_CLOSE } from '../constants/events.constants';
import '../web-components/waitlist-form';
import '../web-components/recommendations-carousel';
import '../web-components/wishlist-button';
import '../web-components/inventory-level';
import '../web-components/product-main-images';
import '../web-components/add-to-cart-overlay';
import '../web-components/product-secondary';
import '../web-components/tooltip-popup';
import '../web-components/size-guide';
import { showModal } from '../services/dialog.service';
import groupBy from '../helpers/groupBy';
import { allOptionsSelected, getRecommendationParameters, matchesCurrentAndSelectedOptions, NO_SELECTED_VARIANT, shownVariant } from "./product-shared";
import { addToCart } from "../services/cart.service";
import { emitShopifyEvent } from "../services/analytics.service";
import { transformStorePhone, toRawPhoneNumber } from "../helpers/transformations";
var CTA_TYPES;
(function (CTA_TYPES) {
    CTA_TYPES["CTA_ADD_CART"] = "addCart";
    CTA_TYPES["CTA_SHOP_SIMILAR"] = "shopSimilar";
    CTA_TYPES["CTA_NOTIFY_ME"] = "notifyMe";
})(CTA_TYPES || (CTA_TYPES = {}));
class ProductMain extends HTMLElement {
    constructor() {
        super();
        this.subscriptions = new Subscription();
    }
    connectedCallback() {
        console.log("product-main connected");
        const selectedVariant$ = new BehaviorSubject(undefined);
        this.selectedProductId$ = new BehaviorSubject(window.productId);
        const mode = this.getAttribute("mode");
        const pdpType = this.getAttribute("type");
        const searchParams = new URLSearchParams(window.location.search);
        const skuParam = searchParams.get('sku');
        const variantParam = +searchParams.get('variant');
        let initSelectedVariant = null;
        if (skuParam) {
            initSelectedVariant = allVariants().find((v) => v.sku === skuParam);
        }
        else if (variantParam) {
            initSelectedVariant = allVariants().find((v) => v.variantID === variantParam);
        }
        const initialParamSelections = getInitialSelectionsFromSearchParams(window.location.search);
        const combinedInitialSelections = { ...window.initialDefaultSelections, ...initialParamSelections };
        let initialSelections = window.initialDefaultSelections;
        if (initSelectedVariant) {
            initialSelections = initSelectedVariant.optionsMap;
        }
        else if (getProductVariantsFromOptions(combinedInitialSelections, this.selectedProductId$.getValue()).length) {
            initialSelections = combinedInitialSelections;
        }
        const selectedOptions$ = new BehaviorSubject(initialSelections);
        // temporarily link to separate products on color button click
        const linkClickSub$ = fromEvent(this, "click", (e) => {
            const target = e.target;
            const productUrl = target.dataset.productUrl;
            if (productUrl && this.selectedProductId$.getValue() !== +target.dataset.productId) {
                if (mode === "quickshop") {
                    const refreshEvent = new CustomEvent(QUICKSHOP_REFRESH, { detail: { handle: target.dataset.productUrl.split("/").slice(-1)[0] } });
                    window.dispatchEvent(refreshEvent);
                }
                else {
                    const urlParams = buildUrlParamsFromSelectedOptions(selectedOptions$.getValue());
                    location.href = `${productUrl}?${urlParams}`;
                }
            }
        }).subscribe();
        const selectedOptionsSub$ = selectedOptions$.pipe(tap(selectedOptions => console.log("selected options", selectedOptions)), filter(selectedOptions => !!selectedOptions), tap((selectedOptions) => this.updateProductOptionsDisplay(selectedOptions, pdpType)), tap((selectedOptions) => {
            if (allOptionsSelected(selectedOptions)) {
                const selectedVariant = getProductVariantsFromOptions(selectedOptions, this.selectedProductId$.getValue())[0];
                selectedVariant$.next(selectedVariant);
            }
            else {
                selectedVariant$.next(NO_SELECTED_VARIANT);
            }
        }), tap(removeErrorMessage)).subscribe();
        const selectedVariantSub$ = selectedVariant$.pipe(tap(variant => console.log("selected variant", variant)), 
        // adding selected variant info as attribute to help with analytics (QM)
        tap(variant => variant ? this.setAttribute("selected-variant", JSON.stringify(variant)) : this.removeAttribute("selected-variant")), tap(variant => {
            let ctaTypes;
            const inventoryLevelEl = this.querySelector('inventory-level');
            if (inventoryLevelEl) {
                inventoryLevelEl.setAttribute('level', '');
            }
            if (variant) {
                if (variant.available) {
                    ctaTypes = [CTA_TYPES.CTA_ADD_CART];
                }
                else {
                    ctaTypes = [CTA_TYPES.CTA_SHOP_SIMILAR, CTA_TYPES.CTA_NOTIFY_ME];
                }
                if (inventoryLevelEl) {
                    inventoryLevelEl.setAttribute('display', 'show');
                    inventoryLevelEl.setAttribute('level', variant.inventoryLevel);
                }
            }
            else {
                ctaTypes = [CTA_TYPES.CTA_ADD_CART];
                if (inventoryLevelEl) {
                    inventoryLevelEl.setAttribute('display', 'hide');
                }
            }
            this.updateProductCtas(ctaTypes);
        }), tap(variant => {
            if (window.storeAvailability) {
                const collectionAvailabilityMessageEl = this.querySelector('.product__store-availability-message');
                if (collectionAvailabilityMessageEl) {
                    const storeName = collectionAvailabilityMessageEl.dataset.storeName || 'the store';
                    const storePhone = collectionAvailabilityMessageEl.dataset.storePhone || null;
                    const rawStorePhone = toRawPhoneNumber(storePhone) || null;
                    const displayStorePhone = transformStorePhone(storePhone) || null;
                    const phoneMessage = (rawStorePhone && displayStorePhone) ? window.productUi.storeInventory.phoneMessage.replace('{rawPhone}', rawStorePhone).replace('{displayPhone}', displayStorePhone) : window.productUi.storeInventory.noPhoneMessage;
                    let message = window.productUi.storeInventory.noVariantSelected;
                    if (variant) {
                        const variantStoreAvailability = window.storeAvailability.atp[variant.sku] > 0;
                        message = variantStoreAvailability ? window.productUi.storeInventory.productInStock : window.productUi.storeInventory.productOutOfStock;
                    }
                    collectionAvailabilityMessageEl.innerHTML = message.replace('{storeName}', storeName).replace('{phoneMessage}', phoneMessage);
                }
            }
        }), tap(updateAnalytics)).subscribe();
        const optionClickSub$ = fromEvent(this, "click", (e) => {
            const target = e.target;
            if (target.matches('.js-product-option:not([data-product-url])')) {
                const { optionName, optionValue } = target.dataset;
                const currentSelection = selectedOptions$.getValue();
                const updateValue = isDeselecting(target, currentSelection[optionName], optionValue) ? undefined : optionValue;
                if (updateValue) {
                    emitShopifyEvent("untuckit:product_option_selected", { optionName, optionValue: updateValue });
                }
                selectedOptions$.next({ ...selectedOptions$.value || {}, ...{ [optionName]: updateValue } });
            }
        }).subscribe();
        const sizeGuideSub$ = fromEvent(this, "click", (e) => {
            const target = e.target;
            if (getElementOrParentByClassName(target, 'product__open-size-guide')) {
                const template = document.getElementById("product-size-guide");
                if (template) {
                    const fragment = template.content.cloneNode(true);
                    const sizeGuideEl = fragment.firstElementChild;
                    showModal([sizeGuideEl], { classes: "size-guide" });
                }
            }
        }).subscribe();
        const ctaClickSub$ = fromEvent(this, "click", (e) => {
            const target = e.target;
            if (target.classList.contains('js-product-cta')) {
                const action = target.dataset.action;
                switch (action) {
                    case CTA_TYPES.CTA_ADD_CART: {
                        const selectedVariant = selectedVariant$.getValue();
                        if (selectedVariant) {
                            if (pdpType === 'bundle') {
                                const itemsToAdd = Object.values(window.bundleVariantGroups).map((variants) => {
                                    const found = variants.find((v) => {
                                        const keys = Object.keys(selectedVariant.optionsMap);
                                        return keys.every((k) => v.optionsMap[k] === selectedVariant.optionsMap[k]);
                                    });
                                    return found;
                                }).map((variant) => {
                                    return {
                                        id: variant.variantID,
                                        quantity: 1
                                    };
                                });
                                addToCart({ items: itemsToAdd }).then((data) => {
                                    console.log("addItemsToCart", data);
                                    showInlineCart();
                                    closeQuickshop();
                                });
                            }
                            else {
                                const properties = {};
                                const recipientEmailEl = document.getElementById('gift-card-recipient-email');
                                if (recipientEmailEl) {
                                    const email = recipientEmailEl.value;
                                    if (recipientEmailEl.checkValidity()) {
                                        properties["Recipient email address"] = email;
                                        properties["_gct-id"] = generateGiftCardId();
                                        removeErrorMessage();
                                    }
                                    else {
                                        const error = email ? 'Please enter a valid email address.' : 'Please enter an email address.';
                                        showErrorMessage(error);
                                        return;
                                    }
                                }
                                addToCart({ items: [{ id: selectedVariant.variantID, quantity: 1, properties }] }).then(handleAddToCart)
                                    .catch((err) => { console.error(err); });
                            }
                        }
                        else {
                            const error = generateErrorFromProductOptions(selectedOptions$.getValue());
                            showErrorMessage(error);
                        }
                        break;
                    }
                    case CTA_TYPES.CTA_NOTIFY_ME: {
                        const waitlistFormEl = document.createElement('waitlist-form');
                        const selectedVariant = selectedVariant$.getValue();
                        waitlistFormEl.setAttribute('variantId', `${selectedVariant.variantID}`);
                        waitlistFormEl.setAttribute('productId', `${selectedVariant.productId}`);
                        const waitlistTitle = this.getAttribute('is-coming-soon') === 'true' ? window.productUi.waitlist.comingSoon : window.productUi.waitlist.default;
                        waitlistFormEl.setAttribute('title', waitlistTitle);
                        const els = [waitlistFormEl];
                        if (!isMobile()) {
                            els.push(generateRecommendationCarousel());
                        }
                        showModal(els);
                        break;
                    }
                    case CTA_TYPES.CTA_SHOP_SIMILAR: {
                        const recommendationCarousel = generateRecommendationCarousel();
                        showModal([recommendationCarousel]);
                        break;
                    }
                    default:
                        console.error(`unable to determine action for cta click: ${action}`);
                        break;
                }
            }
        }).subscribe();
        const backInStockClick$ = fromEvent(this, "click", (e) => {
            var _a, _b, _c, _d;
            const target = e.target;
            if (getElementOrParentByClassName(target, 'js-back-in-stock-popup')) {
                const selectedVariant = selectedVariant$.getValue();
                if (selectedVariant) {
                    (_b = (_a = window.BIS) === null || _a === void 0 ? void 0 : _a.popup) === null || _b === void 0 ? void 0 : _b.show({ variantId: selectedVariant.variantID });
                }
                else {
                    (_d = (_c = window.BIS) === null || _c === void 0 ? void 0 : _c.popup) === null || _d === void 0 ? void 0 : _d.show();
                }
            }
        }).subscribe();
        const analyticsClicks$ = fromEvent(this, "click", (e) => {
            const target = e.target;
            if (getElementOrParentByClassName(target, 'single-tab__button')) {
                emitShopifyEvent('untuckit:product_detail_tab_clicked', { tabName: target.textContent.trim() });
            }
        }).subscribe();
        function generateRecommendationCarousel() {
            const el = document.createElement('recommendations-carousel');
            const selectedOptions = selectedOptions$.getValue();
            const recommendationParameters = getRecommendationParameters({ ...selectedOptions, ...{ gender: window.productDepartment, sku: selectedVariant$.getValue().sku } });
            Object.entries(recommendationParameters).forEach((entry) => {
                const [key, val] = entry;
                el.setAttribute(key, val);
            });
            return el;
        }
        function handleAddToCart(response) {
            var _a;
            if (window.UNTUCKitConfig.showAddToCartOverlay) {
                if ((_a = response === null || response === void 0 ? void 0 : response.items) === null || _a === void 0 ? void 0 : _a.length) {
                    const addToCartOverlay = document.createElement('add-to-cart-overlay');
                    addToCartOverlay.setAttribute('cart-line-item-data', JSON.stringify(response.items[0]));
                    const selectedVariant = selectedVariant$.getValue();
                    const recommendationParameters = getRecommendationParameters({ fit: selectedVariant.optionsMap.fit, size: selectedVariant.optionsMap.size, gender: window.productDepartment, sku: selectedVariant.sku });
                    addToCartOverlay.setAttribute('recommendation-parameters', JSON.stringify(recommendationParameters));
                    showModal([addToCartOverlay], { classes: 'add-to-cart-overlay' });
                }
                else {
                    console.error("item added to cart successfully but no data returned");
                }
            }
            else {
                showInlineCart();
            }
            closeQuickshop();
        }
        function closeQuickshop() {
            if (mode === 'quickshop') {
                const closeEvent = new CustomEvent(QUICKSHOP_CLOSE);
                window.dispatchEvent(closeEvent);
            }
        }
        const fullDetailsLinkEl = this.querySelector('.product__full-details-link');
        if (fullDetailsLinkEl && window.collectionHandle && window.productHandle) {
            fullDetailsLinkEl.href = `/collections/${window.collectionHandle}/products/${window.productHandle}`;
        }
        // add to top-level observable so we can unsubscribe easily on disconnect
        this.subscriptions.add(linkClickSub$);
        this.subscriptions.add(selectedOptionsSub$);
        this.subscriptions.add(selectedVariantSub$);
        this.subscriptions.add(optionClickSub$);
        this.subscriptions.add(ctaClickSub$);
        this.subscriptions.add(sizeGuideSub$);
        this.subscriptions.add(backInStockClick$);
        this.subscriptions.add(analyticsClicks$);
    }
    disconnectedCallback() {
        console.log("product-main disconnected");
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    }
    updateProductCtas(ctaTypes) {
        console.log("updating product ctas", ctaTypes);
        const container = this.querySelector('.product__cta-container');
        const ctaTemplate = document.getElementById('product-cta-template');
        if (container && ctaTemplate) {
            container.innerHTML = '';
            const newEls = ctaTypes.map((t, i) => {
                const fragment = ctaTemplate.content.cloneNode(true);
                const el = fragment.firstElementChild;
                el.dataset.action = t;
                el.textContent = getCtaText(t);
                if (i === 0) {
                    el.classList.add('primary');
                }
                else {
                    el.classList.add('secondary');
                }
                return el;
            });
            container.append(...newEls);
        }
    }
    updateProductOptionsDisplay(selectedOptions, type) {
        Object.entries(selectedOptions).forEach((opt) => {
            const [optionType, optionValue] = opt;
            const currentValueEl = this.querySelector(`.product-option__container--${optionType} .product-option__current-value`);
            if (currentValueEl) {
                const optDisplayValue = window.optionHandleToValue[optionValue];
                currentValueEl.textContent = optionValue && optDisplayValue ? `: ${optDisplayValue}` : '';
            }
        });
        this.querySelectorAll('.js-product-option:not(.product-option__value-button--custom)').forEach((el) => {
            el.classList.remove('product-option--selected', 'product-option--out-of-stock', 'product-option--unavailable'); // reset style classes
            const { optionName, optionValue, productId } = el.dataset;
            const isSameValue = selectedOptions[optionName] === optionValue;
            const isSameProduct = productId ? +productId === this.selectedProductId$.getValue() : true;
            if (isSameValue && isSameProduct) {
                el.classList.add('product-option--selected');
            }
            if (type === "bundle") {
                const associatedVariants = getAssociatedBundleVariants(optionName, optionValue, selectedOptions);
                if (associatedVariants.length) {
                    const allProductsAvailable = associatedVariants.some(variants => {
                        return variants.reduce((acc, curr) => acc && curr.available, true);
                    });
                    if (!allProductsAvailable) {
                        el.classList.add("product-option--unavailable"); // make unavailable for now until we determine how to handle out of stock bundles
                    }
                }
                else {
                    el.classList.add("product-option--unavailable");
                }
            }
            else {
                const associatedVariants = getAssociatedVariants(optionName, optionValue, selectedOptions, this.selectedProductId$.getValue());
                if (associatedVariants.length) {
                    // don't update out-of-stock status display for products in a store collection as the site only takes into account online inventory for now
                    if (this.getAttribute('under_store_collection') === 'true') {
                        return;
                    }
                    if (!associatedVariants.find(av => av.available)) {
                        el.classList.add("product-option--out-of-stock");
                    }
                }
                else {
                    el.classList.add("product-option--unavailable");
                }
            }
        });
    }
}
if (!window.customElements.get("product-main")) {
    customElements.define("product-main", ProductMain);
}
function buildUrlParamsFromSelectedOptions(options) {
    return Object.entries(options).filter(e => {
        const [key, val] = e;
        return key !== 'color' && !!val;
    }).reduce((acc, curr) => {
        const [key, val] = curr;
        return `${acc}${acc ? "&" : ''}${key}=${val}`;
    }, '');
}
function getInitialSelectionsFromSearchParams(str) {
    const urlParams = new URLSearchParams(str);
    const values = {};
    Object.keys(window.initialDefaultSelections).forEach((prop) => {
        if (urlParams.has(prop)) {
            values[prop] = urlParams.get(prop);
        }
    });
    return values;
}
function allVariants() {
    return window.variantsArray.filter(shownVariant);
}
function allBundledVariants() {
    return Object.values(window.bundleVariantGroups).flat().filter(shownVariant);
}
function getCtaText(ctaType) {
    return window.productUi.ctas[ctaType];
}
function getProductVariantsFromOptions(selectedOptions, currentProductId) {
    return allVariants().filter(v => v.productId === currentProductId).filter(v => {
        return Object.entries(selectedOptions).filter(entry => !!entry[1]).reduce((acc, curr) => {
            const [key, val] = curr;
            return acc && v.optionsMap[key] === val;
        }, true);
    });
}
function isDeselecting(target, currentVal, selectedVal) {
    return !target.dataset.productUrl && currentVal === selectedVal;
}
function updateAnalytics(variant) {
    var _a;
    if (variant) {
        if ((_a = window.DY) === null || _a === void 0 ? void 0 : _a.recommendationContext) {
            window.DY.recommendationContext = { type: 'PRODUCT', data: [variant.sku] };
        }
    }
}
function getAssociatedVariants(optionName, optionValue, selectedOptions, productId) {
    let associatedVariants = allVariants().filter(v => matchesCurrentAndSelectedOptions(v, optionName, optionValue, selectedOptions));
    if (optionName !== 'color') {
        associatedVariants = associatedVariants.filter(v => v.productId === productId);
    }
    return associatedVariants;
}
function getAssociatedBundleVariants(optionName, optionValue, selectedOptions) {
    const numProducts = Object.keys(window.bundleVariantGroups).length;
    const groupingFunc = (v) => {
        return Object.entries(v.optionsMap)
            .filter(e => e[0] !== 'color')
            .map(e => e[1])
            .reduce((acc, curr) => acc += curr, '');
    };
    const groups = groupBy(allBundledVariants(), groupingFunc);
    return Object.values(groups)
        .filter(variants => variants.length && variants.length === numProducts)
        .filter(variants => matchesCurrentAndSelectedOptions(variants[0], optionName, optionValue, selectedOptions));
}
function generateErrorFromProductOptions(options) {
    const missingOptions = Object.entries(options).filter(e => !e[1]);
    let errorMessage = 'Please select all options.';
    if (missingOptions.length && missingOptions.length <= 2) {
        const optionText = missingOptions.reduce((acc, curr) => {
            const [key] = curr;
            const msg = `${acc ? ' and ' : ''}${key.replace("-", " ")}`;
            return acc + msg;
        }, '');
        errorMessage = `Please select ${optionText.indexOf('and') > -1 ? 'both' : ['a', 'e', 'i', 'o', 'u'].indexOf(optionText[0]) > -1 ? 'an' : 'a'} ${optionText}.`;
    }
    return errorMessage;
}
function removeErrorMessage() {
    const errorEl = document.querySelector('.product__error-message');
    if (errorEl) {
        errorEl.textContent = '';
        errorEl.classList.add('hidden');
    }
}
function showErrorMessage(err) {
    const errorEl = document.querySelector('.product__error-message');
    if (errorEl) {
        errorEl.textContent = err;
        errorEl.classList.remove('hidden');
    }
}
export const _forTesting = {
    getAssociatedVariants,
    generateErrorFromProductOptions
};
function generateGiftCardId() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}
